import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { SearchService } from '@app/search/services';
import { TranslateService } from '@ngx-translate/core';
import { getSelectedFilterCount } from '@app/search/components/search-view/search-reactive-store/utils';
import {
  A11yService,
  AuthService,
  SearchNavigationService,
} from '@app/shared/services';
import { Observable, map, tap } from 'rxjs';
import { OrgHelpMenuService } from '@app/orgs/services/org-help-menu.service';
import { SearchFilterFilter } from '@app/search/search-api.model';

@Component({
  selector: 'dgx-search-no-results',
  templateUrl: './search-no-results.component.html',
  styleUrls: ['./search-no-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchNoResultsComponent {
  @Input() public hasError: boolean;
  @Input() public searchTerm: string;
  @Input() public filters: SearchFilterFilter[] = [];

  constructor(
    private authService: AuthService,
    private searchService: SearchService,
    private translateService: TranslateService,
    private searchNavigationService: SearchNavigationService,
    private orgHelpMenuService: OrgHelpMenuService,
    private a11yService: A11yService,
    private cdr: ChangeDetectorRef
  ) {}

  public get layout(): EmptyResultsLayout {
    return this.hasError ? 'error' : this.filtersApplied ? 'filtered' : 'empty';
  }

  public get emptyImageURL(): string {
    return this.layout === 'filtered'
      ? this.searchService.getEmptyFilteredImageUrl()
      : this.searchService.getEmptyImageUrl();
  }

  public get filtersApplied() {
    return this.appliedFacetCount > 0;
  }

  public get i18n(): { [key: string]: string } {
    return this.translateService.instant(
      [
        'Core_Empty_Image_Alt',
        'Core_NoResultsMatch',
        'Core_LanguageSettings',
        'LearningSearch_ErrorTitle',
        'LearningSearch_ErrorDescription',
        'LearningSearch_NoResultsFoundAdvice',
        'LearningSearch_NoResultsFoundMsg',
        'LearningSearch_ResetFiltersAdvice',
      ],
      {
        searchTerm: this.searchTerm, // for "LearningSearch_NoResultsFoundMsg"
      }
    );
  }

  public get noResultsFoundMessage(): string {
    return {
      error: this.i18n.LearningSearch_ErrorTitle,
      filtered: this.i18n.Core_NoResultsMatch,
      empty: this.i18n.LearningSearch_NoResultsFoundMsg,
    }[this.layout];
  }

  public get noResultsFoundAdvice(): string {
    return {
      error: this.i18n.LearningSearch_ErrorDescription,
      filtered: this.i18n.LearningSearch_ResetFiltersAdvice,
      empty: this.i18n.LearningSearch_NoResultsFoundAdvice,
    }[this.layout];
  }

  private get appliedFacetCount() {
    return getSelectedFilterCount(this.filters);
  }

  public get supportMessage$(): Observable<string> {
    return this.orgHelpMenuService.orgSupportInfo$.pipe(
      map((supportInfo) => {
        return supportInfo && this.hasError
          ? this.getContactSupportMessage(supportInfo.email)
          : null;
      }),
      tap(() => this.cdr.markForCheck())
    );
  }

  public get languageSettingsUrl(): string {
    const username = this.authService?.authUser?.viewerProfile.vanityUrl;

    return `/profile/${username}/settings/account`;
  }

  public get resetFiltersText(): string {
    return this.translateService.instant('Core_ResetFiltersWithCount', {
      count: this.appliedFacetCount,
    });
  }

  public ngOnInit(): void {
    if (this.authService.isOrgUser) {
      this.orgHelpMenuService.getSupportInfo(
        this.authService.authUser.defaultOrgId
      );
    }

    // Announce error for a11y users
    if (this.hasError) {
      this.a11yService.announcePolite(this.i18n.LearningSearch_ErrorTitle);
    }
  }

  public clearFilters() {
    this.searchNavigationService.searchLearnings({
      appliedFacets: [],
    });
  }

  private getContactSupportMessage(email: string): string {
    return this.translateService.instant('LearningSearch_ErrorContactSupport', {
      hrefStart: `<a class="color-blue uhover" href="mailto:${email}">`,
      hrefEnd: '</a>',
    });
  }
}

type EmptyResultsLayout =
  | 'error' // there was an error, probably a failed API call
  | 'filtered' // filters were applied, but no results were found
  | 'empty'; // no filters were applied, and no results were found
